import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import _ from 'lodash';
import objectSupport from 'dayjs/plugin/objectSupport';
import randomString from 'random-string';

import PropTypes from 'prop-types';

import {
    ProviderHeader,
    GoBackButton,
    Tag,
    Text,
    ModeratorCard,
    SessionDetailCard,
    MemberCard,
    CheckInCard,
    RulesCard,
    ShareLinkCard,
    PDPADialog,
} from '../../components/index';

import EditSessionDialog from '../../components/Provider/Dialog/EditSessionDialog';

import {
    providerPageWidth,
    userRole,
    SESSION_COLORS,
    SESSION_TYPE,
    GROUP_RULES_TH,
    OPEN_RULES_TH,
} from '../../constant';

import { GET_SESSION_DETAL_MEET, canStart, updateRoomId } from '../../services/provider';

import { createRoom, joinRoom } from '../../services';

import { alertError } from '../../redux/stateActions';

import socket from '../../socket';

dayjs.extend(objectSupport);

const GroupOpenSessionDetail = ({ history, location }) => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);

    const statusRef = useRef(null);
    const [maxHeight, setMaxHeight] = useState(651);

    useEffect(() => {
        const elementHeight = statusRef.current.clientHeight;

        setMaxHeight(elementHeight);
    }, []);

    const [meetEnd, setMeetEnd] = useState(false);

    const [checkIn, setCheckIn] = useState([]);
    const [switchState, setSwitchState] = useState({
        ready: false,
        speakToText: false,
        soundRecord: false,
    });
    const [rules, setRules] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleGoBack = () => {
        if (location.state?.prevPath) {
            const path = {
                pathname: location.state.prevPath,
                state: {
                    tab: location.state.prevTab,
                },
            };
            history.push(path);
        } else {
            history.goBack();
        }
    };

    const handleSwitchChange = (event) => {
        const name = event.target.name;
        const checked = event.target.checked;
        setSwitchState({ ...switchState, [name]: checked });
    };

    /*--------------- Get Meet ---------------*/
    const [meetData, setMeetData] = useState({});
    const [sessionData, setSessionData] = useState({});
    const [participants, setParticipants] = useState([]);

    const {
        data: getMeetData,
        error: getMeetError,
        refetch: getMeetRefetch,
    } = useQuery(GET_SESSION_DETAL_MEET, {
        variables: { id: id },
        skip: !id,
        pollInterval: 30000,
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (getMeetError) {
            dispatch(alertError(`Cannot get Meet data: ${getMeetError.message}`));
        }

        if (getMeetData && _.isEmpty(getMeetData.meet)) {
            dispatch(alertError(`Meet not found`));
            handleGoBack();
        }

        if (getMeetData && !_.isEmpty(getMeetData.meet)) {
            const { rules, patients, sessions, type } = getMeetData.meet;

            setMeetData(getMeetData.meet);
            setSessionData(sessions[0]);

            let selectedRules = [];
            if (type === 'group') {
                selectedRules = GROUP_RULES_TH.filter((r) => rules.includes(r.id));
            }
            if (type === 'open') {
                selectedRules = OPEN_RULES_TH.filter((r) => rules.includes(r.id));
            }
            setRules(selectedRules);

            setParticipants(patients);

            const checkedInPatient = sessions[0].patient_summaries.map((item) => ({
                ...item.patient,
                checkinTime: item.checkinTime,
            }));

            setCheckIn(checkedInPatient);
        }
    }, [getMeetError, getMeetData]);

    useEffect(() => {
        if (!_.isEmpty(meetData) && !_.isEmpty(sessionData)) {
            socket.emit('joinOpenSession', { sessionId: sessionData.id });

            if (meetData.cancelled) {
                return setMeetEnd(true);
            }

            if (sessionData.sessionEnd) {
                return setMeetEnd(true);
            }

            if (!sessionData.roomId) {
                const checkTime = () => {
                    const endTime = dayjs(meetData.startDate).set({
                        hour: meetData.endTime.split(':')[0],
                        minute: meetData.endTime.split(':')[1],
                    });

                    if (dayjs().isAfter(endTime, 'minute')) {
                        setMeetEnd(true);
                        clearInterval(myInterval);
                    }
                };

                const myInterval = setInterval(checkTime, 1000);
            }

            if (sessionData.roomId && meetData.type === 'open') {
                setMeetEnd(true);
            }
        }
    }, [meetData, sessionData]);

    /*--------------- PDPA ---------------*/
    const [openPDPA, setOpenPDPA] = useState(false);
    const [accepted, setAccepted] = useState(false);

    const onCheckChange = (e) => {
        setAccepted(e.target.checked);
    };

    const handleClosePDPA = () => {
        setOpenPDPA(false);
        setAccepted(false);
    };

    const onAccept = () => {
        setOpenPDPA(false);
        handleCall();
    };

    const handleCall = async () => {
        if (!accepted) {
            setOpenPDPA(true);
            return;
        }

        setLoading(true);

        try {
            if (!sessionData.roomId) {
                const res = await canStart({ id: user.staff.hospital.id, amount: 100 });

                if (res.status === 200) {
                    if (res.data.canStart === false) {
                        setLoading(false);
                        return dispatch(alertError(`Not enough credit`));
                    }

                    handleSetUpRoom(sessionData.id);
                } else {
                    throw new Error(res);
                }
            } else {
                const joinRes = await handleJoinRoom(sessionData.roomId);

                if (joinRes.data) {
                    window.open(
                        `${window.location.origin}/provider/${
                            meetData.type === 'group' ? 'groupcall' : 'opencall'
                        }/${sessionData.id}?roomId=${joinRes.data.roomId}&session=${
                            joinRes.data.session
                        }`,
                        '_blank'
                    );
                }
            }
            setLoading(false);
        } catch (error) {
            setLoading(false);
            dispatch(alertError(`Call Error: ${error.message}`));
        }
    };

    const handleJoinRoom = async (roomId) => {
        const params = new URLSearchParams();
        params.append('roomId', roomId);
        params.append('sessionId', randomString({ length: 16 }).toLowerCase());
        params.append('role', userRole.DOCTOR);
        params.append(
            'displayName',
            `${user.fullname}|${user.staff?.id}|${user.avatar?.url}|Provider`
        );
        params.append('password', '01011999');

        try {
            const joinRes = await joinRoom(roomId, params);

            if (joinRes.data) {
                return joinRes;
            } else {
                throw new Error(joinRes);
            }
        } catch (error) {
            dispatch(alertError(`handleJoinRoom Error: ${error.message}`));
        }
    };

    const handleSetUpRoom = async (sessionId) => {
        try {
            const result = await createRoom();

            if (result.roomId) {
                const joinRes = await handleJoinRoom(result.roomId);

                if (joinRes.data) {
                    const updateRoomIdRes = await updateRoomId({
                        sessionId,
                        roomId: result.roomId,
                    });

                    if (updateRoomIdRes.status === 200) {
                        socket.emit(
                            meetData.type === 'group'
                                ? 'providerSetUpGroupRoom'
                                : 'providerSetUpOpenRoom',
                            {
                                roomId: result.roomId,
                                sessionId: sessionId,
                                patientsId: participants.map((p) => p.id),
                            }
                        );

                        setLoading(false);

                        window.open(
                            `${window.location.origin}/provider/${
                                meetData.type === 'group' ? 'groupcall' : 'opencall'
                            }/${sessionId}?roomId=${joinRes.data.roomId}&session=${
                                joinRes.data.session
                            }`,
                            '_blank'
                        );
                    }
                }
            }
        } catch (error) {
            dispatch(alertError(`handleSetUpRoom Error: ${error.message}`));
        }
        setLoading(false);
    };

    useEffect(() => {
        socket.on('patientCheckedIn', (data) => {
            if (data.meetId === id) {
                getMeetRefetch();
            }
        });

        socket.on('sessionEnd', (data) => {
            if (data.meetId === id) {
                getMeetRefetch();
            }
        });
    }, []);

    const handleDuplicate = (id, type) => {
        history.push(`/provider/calendar/${type}/duplicate/${id}`);
    };

    const handleSessionDetail = (id, type) => {
        history.push(`/provider/calendar/${type}/edit/${id}`);
    };

    return (
        <div
            style={{
                padding: '15px 20px 50px',
                maxWidth: providerPageWidth,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                rowGap: '15px',
                position: 'relative',
            }}
        >
            <PDPADialog
                open={openPDPA}
                onClose={handleClosePDPA}
                accepted={accepted}
                onCheckChange={onCheckChange}
                onAccept={onAccept}
            />

            <ProviderHeader>
                <GoBackButton $text onClick={handleGoBack} />
            </ProviderHeader>
            <div style={{ display: 'flex', alignItems: 'center', columnGap: '20px' }}>
                <Tag $backgroundColor={SESSION_COLORS[meetData?.type]}>
                    <Text $fontSize="10px" $color="white" style={{ textTransform: 'uppercase' }}>
                        {SESSION_TYPE[meetData?.type]}
                    </Text>
                </Tag>
                <Text $fontSize="24px" $bold>
                    Activity name - {meetData?.name}
                </Text>
            </div>

            {meetData.type === 'open' &&
                meetData.sessionType === 'public' &&
                !_.isEmpty(sessionData) && (
                    <ShareLinkCard
                        url={`${window.location.origin}/appointment/open/${sessionData.id}`}
                    />
                )}

            <div
                style={{
                    display: 'grid',
                    columnGap: '20px',
                    gridTemplateColumns: '1fr 1fr 300px',
                }}
            >
                <SessionDetailCard
                    data={meetData}
                    maxHeight={maxHeight}
                    onEdit={() => handleSessionDetail(meetData.id, meetData.type)}
                    disabled={meetEnd}
                    onDuplicate={() => handleDuplicate(meetData.id, meetData.type)}
                />
                <MemberCard
                    data={participants}
                    meetData={meetData}
                    maxHeight={maxHeight}
                    disabled={meetEnd}
                    sendNoti={meetData.type === 'group'}
                />
                <div
                    style={{ display: 'flex', flexDirection: 'column', rowGap: '15px' }}
                    ref={statusRef}
                >
                    <ModeratorCard
                        data={meetData}
                        switchState={switchState}
                        onSwitchChange={handleSwitchChange}
                        disabled={meetEnd}
                    />
                    <CheckInCard
                        data={checkIn}
                        meetData={meetData}
                        onStart={handleCall}
                        loading={loading}
                        disabled={user?.staff?.id !== meetData?.staff?.id || meetEnd}
                    />
                </div>
            </div>
            <RulesCard data={rules} disabled={meetEnd} />
        </div>
    );
};

GroupOpenSessionDetail.propTypes = {
    history: PropTypes.object,
    location: PropTypes.object,
};

export default GroupOpenSessionDetail;
